import qs from 'qs';
import converterOfFieldsFilter from '@/lib/converterOfFieldsFilter';
import { maxBy, max, groupBy, cloneDeep, uniqBy } from 'lodash';
import { defaultDataSource, selectStatistics, delEmptyRegexp } from '@/constant';
import dayjs from 'dayjs';
import reporter from '@/lib/reporter';
import Xlsx from 'xlsx';
import store from '@/store';
import numImg from '@/assets/img/fieldTypeImgs/数值@2x.png';
import dateImg from '@/assets/img/fieldTypeImgs/日期@2x.png';
import textImg from '@/assets/img/fieldTypeImgs/一般标签@2x.png';
import dictImg from '@/assets/img/fieldTypeImgs/分类标签@2x.png';
import nerImg from '@/assets/img/fieldTypeImgs/主体标签@2x.png';
import sortImg from '@/assets/img/fieldTypeImgs/编号@2x.png';
import { Message } from 'element-ui';

export function isArray(val) {
  return Object.prototype.toString.call(val) === '[object Array]';
}

export function isBool(val) {
  return Object.prototype.toString.call(val) === '[object Boolean]';
}
// 下划线转驼峰
export function toCamelCase(key) {
  return key.replace(/_+[a-zA-Z]/g, (str, index) => (index ? str.substr(-1).toUpperCase() : str));
}

// 驼峰转下划线
export function toPascalCase(key) {
  return key.replace(/[A-Z]/g, (str, index) => (index ? '_' + str.substr(-1).toLowerCase() : str));
}

// 格式化url-query
export function formatUrlQuery(query) {
  return query ? `?${qs.stringify(query)}` : '';
}

// 对象format => string
export function jsonStringify(body) {
  if (typeof body === 'string') {
    return body;
  }
  return JSON.stringify(body);
}

// 动态加载资源
export const loadResource = (() => {
  // 缓存加载的promise
  const tagLoadPromise = {};

  // 闭包
  return (type, url) => {
    // 如果加载过则直接返回加载的promise
    if (tagLoadPromise[url]) {
      return tagLoadPromise[url];
    }
    const typeEnum = {
      js: { position: 'body', tagName: 'script', attrs: { src: url, type: 'text/javascript' } },
      css: { position: 'head', tagName: 'link', attrs: { href: url, type: 'text/css', rel: 'stylesheet' } }
    };
    const newTagType = typeEnum[type];
    tagLoadPromise[url] = new Promise((resolve, reject) => {
      const newTagItem = document.createElement(newTagType.tagName);
      Object.keys(newTagType.attrs).forEach(attrKey => {
        newTagItem[attrKey] = newTagType.attrs[attrKey];
      });
      document[newTagType.position].appendChild(newTagItem);
      newTagItem.onload = () => resolve(true);
      newTagItem.onerror = err => {
        reject(err);
        delete tagLoadPromise[url];
      };
    });
    return tagLoadPromise[url];
  };
})();

export function arrayToTree(sNodes, options = {}) {
  let i, l;
  const key = options.key || 'id';
  const parentKey = options.parentKey || 'parentId';
  const childKey = options.childKey || 'children';
  if (!key || key == '' || !sNodes) return [];

  const arr = JSON.parse(JSON.stringify(sNodes));

  if (arr instanceof Array) {
    const r = [];
    const tmpMap = [];
    for (i = 0, l = arr.length; i < l; i++) {
      tmpMap[arr[i][key]] = arr[i];
    }
    for (i = 0, l = arr.length; i < l; i++) {
      if (tmpMap[arr[i][parentKey]] && arr[i][key] != arr[i][parentKey]) {
        if (!tmpMap[arr[i][parentKey]][childKey]) tmpMap[arr[i][parentKey]][childKey] = [];

        tmpMap[arr[i][parentKey]][childKey].push(arr[i]);
      } else {
        r.push(arr[i]);
      }
    }
    return r;
  } else {
    return [arr];
  }
}

export function treeToArray(tree, options = {}) {
  const { level = 0, fullParentId = '' } = options;
  const arr = [];

  tree.forEach(item => {
    arr.push({
      ...item,
      level,
      fullParentId
    });
    if ((item.children || []).length > 0) {
      const childFullParentId = fullParentId ? [...fullParentId.split('/'), item.id].join('/') : item.id;
      arr.push(
        ...treeToArray(item.children, {
          level: level + 1,
          fullParentId: childFullParentId
        })
      );
    }
  });

  return arr;
}

// 该函数会直接修改原数据tree
export function formatParentIds(tree, options = {}) {
  const key = options.key || 'id';
  const parentKeys = `${options.parentKey || 'parentId'}s`;
  const childKey = options.childKey || 'children';
  let index = 0;
  const arr = [...tree];
  while (index < arr.length) {
    const item = arr[index];
    if (!item[parentKeys]) {
      item[parentKeys] = [];
    }
    if ((item[childKey] || []).length > 0) {
      item[childKey].forEach(childItem => {
        if (!childItem[parentKeys]) {
          childItem[parentKeys] = [];
        }
        childItem[parentKeys].unshift(...(item[parentKeys] || []), item[key]);
      });
      arr.push(...(item[childKey] || []));
    }
    index++;
  }
  return tree;
}

// 根据relation初始化caseLabel
export function initColumns(relationArr) {
  return relationArr.map(item => {
    let parentId = item._source.parentId;
    if (!parentId && item._source.system) {
      parentId = 'system_root';
    }
    let dictClassifyValue = (item._source.dictClassifyValue || []).slice(0, 6);
    let dictValue = (item._source.dictValue || []).slice(0, 6);
    if (dictClassifyValue.length > 0) {
      dictValue = dictClassifyValue.map(o => o.labelName);
    } else {
      if (dictValue.length > 0) {
        dictClassifyValue = dictValue.map(text => {
          return { labelName: text, children: '' };
        });
      }
    }

    let indexSort = item._source.index_sort;
    if (!indexSort && indexSort !== 0) {
      indexSort = 99999999;
    }

    return {
      _id: item._id,
      ownerId: item._source.ownerId || '',
      parentId,
      columnKey: item._source.field_name,
      field_name: item._source.field_name,
      title: item._source.chinese_name,
      property: item._source.property || '',
      word: item._source.word || '',
      regInfoArr: item._source.regInfoArr || [],
      exclude_word: item._source.exclude_word || '',
      exclude_sentence: item._source.exclude_sentence || '',
      type: item._source.field_type,
      deputy_type: item._source.deputy_field_type || '',
      numUnit: item._source.numUnit || [],
      numClassifyUnit: item._source.numClassifyUnit || [],
      numUnitDefault: item._source.numUnitDefault || '',
      dictClassifyValue,
      dictValue, // 用作预览，全部分类值需要通过接口获取
      entityName: item._source.entityName || '',
      full_path: item._source.full_path,
      full_name: item._source.full_name,
      calculator_type: item._source.calculator_type || '',
      isCalculator: [2, 3].includes(item._source.calculator_type),
      code: item._source.code || '',
      calculator_code: item._source.calculator_code || '',
      system: !!item._source.system,
      is_pattern: item._source.is_pattern,
      paragraphArr: item._source.paragraphArr || [],
      base_caseType: item._source.base_caseType || [],
      procedureType: item._source.procedureType || [],
      child_count: item.child_count || 0,
      is_show: item._source.is_show === undefined ? 1 : item._source.is_show,
      groups: item._source.groups,
      sys_id: item._source.sys_id || '',
      coursewareId: item._source.coursewareId || '',
      updateUser: item._source.updateUser,
      isGroupField: item._source.system ? !!item._source.isGroupField : false,
      groupRelationId: item._source.groupRelationId || 'case',
      index_sort: indexSort
    };
  });
}

export function formatColumnToFormInfo(ruleItem = {}) {
  let type = ruleItem.type || 'text';
  if (ruleItem.deputy_type) {
    type = `${type}_${ruleItem.deputy_type}`;
  }

  let numClassifyUnit = cloneDeep(ruleItem.numClassifyUnit || []);
  if (numClassifyUnit.length < 1 && ruleItem.numUnit) {
    numClassifyUnit = ruleItem.numUnit.map(unitText => {
      return { unit: unitText, reg: '', transform: '', default: false };
    });
  }
  return {
    name: ruleItem.title || '',
    word: ruleItem.word || '',
    regInfoArr: cloneDeep(ruleItem.regInfoArr || []),
    exclude_word: ruleItem.exclude_word || '',
    exclude_sentence: ruleItem.exclude_sentence || '',
    type,
    calculator_type: ruleItem.calculator_type || '',
    paragraphArr: [...(ruleItem.paragraphArr || [])],
    base_caseType: [...(ruleItem.base_caseType || [])],
    procedureType: [...(ruleItem.procedureType || [])],
    numClassifyUnit,
    dictClassifyValue: [], // 需要请求单独拿，量比较大
    code: ruleItem.code || '',
    calculator_code: ruleItem.calculator_code || '',
    groupRelationId: ruleItem.groupRelationId || 'case'
  };
}

export function clearColumnInvalidAttr(info) {
  let {
    word,
    exclude_word: excludeWord,
    exclude_sentence: excludeSentence,
    code,
    calculator_code: calculatorCode,
    paragraphArr,
    calculator_type: calculatorType,
    dictClassifyValue,
    numClassifyUnit,
    regInfoArr
  } = info;

  if (calculatorType === 1) {
    code = '';
    calculatorCode = '';
    // regInfoArr = [];
  } else if (calculatorType === 4) {
    // word = '';
    // paragraphArr = [];
    // excludeWord = '';
    // excludeSentence = '';
    code = '';
    calculatorCode = '';
  } else if ([2, 3].includes(calculatorType)) {
    if ((!code && calculatorType === 2) || (!calculatorCode && calculatorType === 3)) {
      return { errMsg: calculatorType === 2 ? '请输入Python计算代码' : '请输入计算公式', info: null };
    }
    word = '';
    excludeWord = '';
    excludeSentence = '';
    paragraphArr = [];
    regInfoArr = [];
  } else {
    word = '';
    excludeWord = '';
    excludeSentence = '';
    paragraphArr = [];
    regInfoArr = [];
    code = '';
    calculatorCode = '';
  }

  if (info.type !== 'text_dict') {
    dictClassifyValue = [];
  }
  if ((info.type || '').slice(0, 3) !== 'num') {
    numClassifyUnit = [];
  }

  word = (word || '').trim();
  excludeWord = (excludeWord || '').trim();
  excludeSentence = (excludeSentence || '').trim();

  return {
    info: {
      ...info,
      code,
      calculator_code: calculatorCode,
      word,
      exclude_word: excludeWord,
      exclude_sentence: excludeSentence,
      paragraphArr,
      regInfoArr,
      dictClassifyValue,
      numClassifyUnit
    }
  };
}

export function transformColumnToSource({ info, typeList }) {
  const { fieldType, deputyFieldType, entityName } = splitType(info.type, typeList);
  const userInfo = store.getters.userInfo;

  const numUnitDefaultItem = (info.numClassifyUnit || []).find(o => o.default);

  return {
    ...info,
    sys_id: '',
    updateUser: {
      id: userInfo.id,
      name: userInfo.name,
      headimgurl: userInfo.headimgurl
    },
    dictValue: (info.dictClassifyValue || []).slice(0, 6).map(o => o.labelName),
    numUnit: (info.numClassifyUnit || []).map(o => o.unit),
    numUnitDefault: numUnitDefaultItem ? numUnitDefaultItem.unit : info.numUnitDefault || '',
    chinese_name: info.name,
    field_type: fieldType,
    deputy_field_type: deputyFieldType,
    entityName,
    update_time: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSSSS')
  };
}

export function splitType(type, typeList = []) {
  const typeStrArr = type.split('_');
  const fieldType = typeStrArr[0];
  const deputyFieldType = typeStrArr.slice(1).join('_');
  let entityName = '';

  if (deputyFieldType && deputyFieldType !== 'dict') {
    const groupItem = typeList.find(typeItem => typeItem.key === `${fieldType}_group`);
    if (groupItem) {
      const item = groupItem.children.find(subTypeItem => subTypeItem.type === type);
      entityName = item.label;
    }
  }

  return { fieldType, deputyFieldType, entityName };
}

export function upgradeFieldForCourse(newFieldItem, courseDetail) {
  const upgradeFields = fields => {
    return (fields || []).map(fieldItem => {
      if (fieldItem._id === newFieldItem._id) {
        return newFieldItem;
      }
      return fieldItem;
    });
  };

  const charts = (courseDetail._source.chart || []).map(chartItem => {
    return { ...chartItem, fields: upgradeFields(chartItem.fields) };
  });

  const newCourseSource = {
    ...courseDetail._source,
    chart: charts,
    fields: upgradeFields(courseDetail._source.fields)
  };

  return newCourseSource;
}

// 寻找所有上级
export function fetchParentColumns(allColumns, columns) {
  const parentColumns = {};
  columns.forEach(item => {
    const parentIds = [item.parentId];
    for (let i = 0; i < parentIds.length; i++) {
      const id = parentIds[i];
      if (!id) {
        continue;
      }
      const parentItem = allColumns.find(itemByAll => itemByAll.columnKey === id);
      if (parentItem && !parentColumns[parentItem.columnKey]) {
        parentIds.push(parentItem.parentId);
        parentColumns[parentItem.columnKey] = parentItem;
      }
    }
  });

  return Object.keys(parentColumns).map(id => parentColumns[id]);
}

/**
 * 离开页面
 * 1.关闭浏览器或者标签，无返回东西
 * 2.离开路由，返回true（确定离开） && false（不离开）
 */
export function leavePage(e) {
  if (e) {
    e.preventDefault();
    e.returnValue = '数据已调整暂未保存，是否确定要离开页面？';
  } else {
    if (confirm('数据已调整暂未保存，是确定要离开页页面？')) {
      return true;
    } else {
      return false;
    }
  }
}

export function checkChartInfo(params) {
  const mustKeys = [
    'columns',
    'rows',
    'colors',
    'statistics',
    'chartConfig',
    'researchCondition',
    'courseCondition',
    'courseId',
    'sortStatistic',
    'relationFilterVal',
    'relationFilterName'
  ];
  const keyOfLost = mustKeys.find(key => {
    if (Object.prototype.hasOwnProperty.bind(params)(key)) {
      return false;
    }
    return true;
  });

  if (keyOfLost) {
    throw new Error(`渲染图表参数缺少 ${keyOfLost}的值`);
  }
  return params;
}

// 默认研究点、推荐研究点解构
export function formatChartInfo({ params, allFieldByRelation }) {
  const rows = [];
  const columns = [];
  const statistics = [];
  const { fields = [], condition = [] } = params;
  fields.forEach(fieldItem => {
    const fieldDetail = allFieldByRelation.find(relationItem => relationItem.columnKey === fieldItem.field);
    if (!fieldDetail) {
      return;
    }

    if (!fieldItem.field_type) {
      fieldItem.field_type = fieldDetail.type;
    }

    if (fieldItem.type === '行') {
      rows.push({
        ...fieldDetail,
        valueOfFilter: converterOfFieldsFilter.toFrontEndVal({ fieldItem, isRow: true, isCol: false })
      });
    }
    if (fieldItem.type === '列') {
      columns.push({
        ...fieldDetail,
        valueOfFilter: converterOfFieldsFilter.toFrontEndVal({ fieldItem, isRow: false, isCol: true })
      });
    }

    if (fieldItem.type === '统计值') {
      const statisticsVal = selectStatistics.find(info => info.value === fieldItem.agg_type) || selectStatistics[0];
      statistics.push({
        ...statisticsVal,
        isStatistic: true,
        fieldDetail
      });
    }
  });

  const conditionTemp = condition
    .map(item => {
      const relationItem = allFieldByRelation.find(relationItem => item.field_name === relationItem.columnKey);
      if (!relationItem) {
        return null;
      }
      return { columnInfo: relationItem, formData: item.formData };
    })
    .filter(item => !!item);

  return {
    rows,
    columns,
    statistics,
    title: params.title,
    type: params.type,
    conditionTemp: JSON.stringify(conditionTemp)
  };
}

// 获取index值最大的对象
export function fetchMaxIndex(arr, options) {
  const { key = 'index_id' } = options || {};
  const result = maxBy(arr, key);
  if (!result) {
    return -1;
  } else {
    return result[key];
  }
}

export function mergeDataSourceHandle({ dataSourceArr }) {
  const result = JSON.parse(defaultDataSource);
  (dataSourceArr || []).forEach(str => {
    const dataSource = JSON.parse(str || defaultDataSource);
    dataSource.query.bool.must.forEach(mustItem => {
      result.query.bool.must.push(mustItem);
    });
    dataSource.query.bool.must_not.forEach(mustNoItem => {
      result.query.bool.must_not.push(mustNoItem);
    });
  });
  return JSON.stringify(result);
}

export function formatDataSourceHandle(dataSource, options) {
  const { includeCleanerFilter = false } = options || {};
  const { query = [] } = dataSource;
  const newQuery = [];
  query.forEach(filterItem => {
    const { byCleaner, isLabelFilter, isTextFilter, info, formData, columnInfo, fieldType, isRecommend } = filterItem;

    if (!includeCleanerFilter && byCleaner) {
      return;
    }

    if (isRecommend) {
      const { fieldDetail } = info;
      newQuery.push({
        formData: {},
        fieldType: fieldDetail.type,
        fieldId: fieldDetail.field_name,
        deputy_field_type: fieldDetail.deputy_type,
        labelType: ['推荐']
      });
    } else if (isLabelFilter) {
      const { fieldDetail, type } = info;
      newQuery.push({
        formData: {},
        marked: type === 'label',
        fieldType: fieldDetail.type,
        fieldId: fieldDetail.field_name,
        deputy_field_type: fieldDetail.deputy_type
      });
    } else if (isTextFilter) {
      const { selects, text } = info;
      newQuery.push({
        formData: { keywordMode: 'input', keyword: text.replace(delEmptyRegexp, ''), rule: 'include' },
        fieldType: 'text',
        fieldId: selects.join('|')
      });
    } else {
      if (fieldType === 'group') {
        newQuery.push({
          fieldType,
          op_type: filterItem.op_type,
          opType: filterItem.op_type,
          query: [...formatDataSourceHandle({ query: filterItem.fieldArr }).query]
        });
      } else {
        const formDataTemp = { ...formData };
        if (formDataTemp.rule !== 'DUPLICATE_COUNT') {
          delete formDataTemp.symbol;
        }
        if (columnInfo.type === 'date' && formDataTemp.rule === 'range') {
          formDataTemp.rule = 'erange';
        }
        if (formDataTemp.date) {
          formDataTemp.date = formDataTemp.date.map(dateVal => {
            if (!dateVal) {
              return dateVal;
            }
            return dayjs(dateVal).format('YYYY-MM-DD');
          });
        }
        if (columnInfo.type === 'num' && (formDataTemp.numArr || []).length > 0) {
          formDataTemp.numArr = formDataTemp.numArr.map(num => {
            return num === '∞' ? null : num;
          });
        }
        newQuery.push({
          formData: formDataTemp,
          fieldType: columnInfo.type,
          deputy_field_type: columnInfo.deputy_type,
          fieldId: columnInfo.field_name
        });
      }
    }
  });

  return { ...dataSource, query: newQuery };
}

export function getSortInfo(sortInfoArr, sortInfoCustom) {
  const sorts = [];

  sortInfoArr.forEach(info => {
    if (!info) {
      return;
    }
    // 默认的可选的option排序没有_id
    sorts.push({
      fieldId: info.fieldInfo._id || info.fieldInfo.columnKey,
      sortType: info.action === 'asc' ? 'ASC' : 'DESC'
    });
  });

  sortInfoCustom.forEach(item => {
    const o = { fieldId: item.fieldInfo._id, sortWay: item.sortWay };
    if (item.multiHandler) {
      o.multiHandler = item.multiHandler;
    }
    sorts.push(o);
  });

  return sorts.length > 0 ? { sorts } : {};
}

export function statisticsMergeColumns({ columns, statistics }) {
  const newStatistics = [...(statistics || [])];
  // y轴标签相当于统计总数
  if ((columns || []).length > 0) {
    columns.forEach(columnInfo => {
      newStatistics.push({ fieldDetail: columnInfo, isStatistic: true, title: '总数', value: 'count' });
    });
  }
  return newStatistics;
}

export function fetchStatisticValKey(statisticItem, relationStatisticItem) {
  let key = '';
  if (statisticItem) {
    key = statisticItem.fieldDetail.title;
    if (statisticItem.value !== 'row_count') {
      key = `${key}(${statisticItem.title})`;
    } else {
      key = `${key}(${relationStatisticItem ? relationStatisticItem.statisticName : '统计值'})`;
    }
  }
  if (!key && relationStatisticItem) {
    key = relationStatisticItem.statisticName;
  }
  return key;
}

export function getRowDefaultCategoryMode(rowItem) {
  const valueOfFilter = JSON.parse(rowItem.valueOfFilter || '{}');

  return 'categoryMode' in valueOfFilter ? valueOfFilter.categoryMode : couldGroupFiled(rowItem);
}

function initRowLabel(label, rowItem) {
  if (label === '-' || rowItem.type !== 'text') {
    return label;
  }

  // 分类分组直接返回，一般分组为 有+标签名
  if (getRowDefaultCategoryMode(rowItem)) {
    return label;
  } else {
    return `${label}${rowItem.title}`;
  }
}

export function formatChartData(params, res, chartType, options) {
  const { rows, statistics, chartConfig = {} } = params;
  const { initLeftRight = true } = options || {};
  const rowTemp = [];
  let columnTemp = [];
  let legendTemp = [];
  let yNameArr = [];

  if (!res) {
    return { rowsData: rowTemp, columnsData: columnTemp, legendsData: legendTemp, chartConfig };
  }

  const { rowsData = [], columnsData = [] } = res;
  const relationStatisticItem = { value: params.relationFilterVal, statisticName: params.relationFilterName };

  try {
    if (rows.length === 0) {
      if (statistics.length <= 1) {
        if (statistics.length === 0) {
          columnTemp = [fetchStatisticValKey(undefined, relationStatisticItem)];
        } else {
          columnTemp = [statistics.map(statisticItem => statisticItem.fieldDetail.title).join('、')];
        }
        legendTemp = columnsData.map(columnItem => columnItem || '-');
        if (statistics.length === 1) {
          yNameArr = [fetchStatisticValKey(undefined, relationStatisticItem)];
        } else {
          yNameArr = [...legendTemp];
        }
        legendTemp.forEach(legendDataItem => {
          rowTemp.push({ name: String(legendDataItem), data: [rowsData[0][legendDataItem] || 0] });
        });
      } else {
        legendTemp = [fetchStatisticValKey(undefined, relationStatisticItem)];
        yNameArr = [...legendTemp];
        columnTemp = statistics.map(statisticItem => statisticItem.fieldDetail.title);
        rowTemp.push({ name: legendTemp[0], data: [] });
        const rowItem = rowsData[0];
        if (rowItem) {
          columnsData.forEach(columnLabel => {
            rowTemp[0].data.push(rowItem[columnLabel]);
          });
        }
      }
    } else if (rows.length === 1) {
      const rowItem = rows[0];
      legendTemp = columnsData.filter(columnDataItem => rowItem.title !== columnDataItem);
      yNameArr = [...legendTemp];
      columnTemp = rowsData.map(rowDataItem => {
        return initRowLabel(rowDataItem[rowItem.title] || '-', rowItem);
      });
      legendTemp.forEach(legendDataItem => {
        const rowTempData = [];
        rowTemp.push({ name: String(legendDataItem), data: rowTempData });
        rowsData.forEach(rowDataItem => rowTempData.push(rowDataItem[legendDataItem] || 0));
      });
    } else if (rows.length === 2) {
      const statisticValKey = fetchStatisticValKey(statistics[0], relationStatisticItem);
      columnTemp = [...new Set(rowsData.map(rowDataItem => initRowLabel(rowDataItem[rows[0].title] || '-', rows[0])))];
      legendTemp = [...new Set(rowsData.map(rowDataItem => initRowLabel(rowDataItem[rows[1].title] || '-', rows[1])))];
      const rowsDataEnum = {};
      rowsData.forEach(rowDataItem => {
        const key = `${initRowLabel(rowDataItem[rows[0].title] || '-', rows[0])}_${initRowLabel(
          rowDataItem[rows[1].title] || '-',
          rows[1]
        )}`;
        rowsDataEnum[key] = rowDataItem[statisticValKey];
      });
      legendTemp.forEach(legendDataItem => {
        const rowTempData = [];
        rowTemp.push({ name: String(legendDataItem), data: rowTempData });
        columnTemp.forEach(columnDataItem => {
          rowTempData.push(rowsDataEnum[`${columnDataItem}_${legendDataItem}`] || 0);
        });
      });
    }

    if (!chartConfig.xName && rows.length > 0) {
      chartConfig.xName = rows[0].title || '';
    }

    columnTemp = columnTemp.map(val => String(val));

    if (['barChart', 'lineChart', 'lineChart2'].includes(chartType)) {
      const maxTextLength = columnTemp.length > 0 ? max(columnTemp.map(columnText => fetchStrLength(columnText))) : 0;
      if (
        rows.length > 0 &&
        (columnTemp.length > 10 || maxTextLength > 12) &&
        !Object.prototype.hasOwnProperty.bind(chartConfig)('rotate')
      ) {
        chartConfig.rotate = -25;
        if (!Object.prototype.hasOwnProperty.bind(chartConfig)('bottom')) {
          chartConfig.bottom = 60;
        }
      }
    } else if (chartType === 'barXChart') {
      if (!Object.prototype.hasOwnProperty.bind(chartConfig)('left')) {
        const maxTextLength = columnTemp.length > 0 ? max(columnTemp.map(columnText => fetchStrLength(columnText))) : 0;
        let leftNum = maxTextLength * 7;
        if (leftNum > 130) {
          leftNum = 130;
        }
        chartConfig.left = leftNum + 20;
      }
    }

    if (initLeftRight) {
      if (['barChart', 'barXChart', 'lineChart', 'lineChart2'].includes(chartType)) {
        if (!Object.prototype.hasOwnProperty.bind(chartConfig)('left')) {
          chartConfig.left = 100;
        }
        if (!Object.prototype.hasOwnProperty.bind(chartConfig)('right')) {
          chartConfig.right = 100;
        }
      }
    }

    yNameArr = filterYNameArr(yNameArr);

    if (yNameArr.length > 1) {
      chartConfig.yNameArr = yNameArr;
    } else {
      if (chartConfig.yName) {
        chartConfig.yNameArr = [chartConfig.yName];
      } else if (yNameArr[0]) {
        chartConfig.yNameArr = [yNameArr[0]];
      }
    }

    if ((chartConfig.yNameArr || []).length < 1) {
      chartConfig.yNameArr = [fetchStatisticValKey(statistics[0], relationStatisticItem)];
    }

    if (!chartConfig.textEllipsis) {
      chartConfig.textEllipsis = 10;
    }

    return { rowsData: rowTemp, columnsData: columnTemp, legendsData: legendTemp, chartConfig };
  } catch (err) {
    console.error(err);
    return { rowsData: [], columnsData: [], legendsData: [], chartConfig };
  }
}

function filterYNameArr(arr) {
  const yNameArr = [];
  const effYnameCount = {};
  arr.forEach(name => {
    const startIndex = name.lastIndexOf('(');
    let yName = name;
    if (startIndex !== -1) {
      yName = name.slice(startIndex + 1, -1);
    }
    if (!effYnameCount[yName]) {
      effYnameCount[yName] = [];
    }
    effYnameCount[yName].push(name);
  });

  Object.keys(effYnameCount).forEach(yName => {
    const nameArr = effYnameCount[yName];
    if (nameArr.length > 1) {
      yNameArr.push(yName);
    } else {
      yNameArr.push(nameArr[0]);
    }
  });
  return yNameArr;
}

function fetchStrLength(text) {
  return (text || '').replace(/[^\u0000-\u00ff]/g, 'aa').length;
}

export function fetchChartQuery({ rows, columns, statistics, relationStatisticItem }) {
  const fields = [...getFieldArr(rows, '行'), ...getFieldArr(columns, '列')];
  const isCaseUnit = relationStatisticItem.value === 'case';
  if ((statistics || []).length > 0) {
    statistics.forEach(statisticItem => {
      let aggType = statisticItem.value;
      if (isCaseUnit && aggType === 'row_count') {
        aggType = 'row_sum';
      }
      fields.push({
        agg_type: aggType,
        field: statisticItem.fieldDetail.columnKey,
        field_name: statisticItem.fieldDetail.title,
        field_type: statisticItem.fieldDetail.type,
        deputy_field_type: statisticItem.fieldDetail.deputy_type,
        type: '统计值'
      });
    });
  } else {
    fields.push({
      agg_type: isCaseUnit ? 'distinct_count' : 'count',
      field: relationStatisticItem.value,
      field_name: relationStatisticItem.statisticName,
      field_type: 'num',
      type: '统计值',
      countGroup: true
    });
  }
  return fields;
}

function getFieldArr(data, type) {
  const arr = [];
  (data || []).forEach(item => {
    const fieldDetail = converterOfFieldsFilter.toQuery({
      fieldDetail: item,
      isRow: type === '行',
      isCol: type === '列'
    });
    if (fieldDetail) {
      const params = { ...fieldDetail };
      let addSize = !!item.size;
      if (item.type === 'num') {
        const valueOfFilter = JSON.parse(item.valueOfFilter || '{}');
        if (valueOfFilter.type !== 'noRange') {
          addSize = false;
        }
      } else {
        addSize = true;
      }
      if (addSize) {
        params.size = item.size || 10;
      }
      arr.push(params);
    }
  });
  return arr;
}

export function checkFieldRun(fieldDetail) {
  if (!fieldDetail.calculator_type) {
    return { pass: false, errMsg: '需设置标签的抽取规则后才能进行标注' };
  }
  if (fieldDetail.calculator_type === 1 && !fieldDetail.word && !fieldDetail.exclude_word) {
    return { pass: false, errMsg: '需设置标签的数据清洗规则后才能进行标注' };
  }
  if (fieldDetail.calculator_type === 2 && !fieldDetail.code) {
    return { pass: false, errMsg: '需设置标签的编码计算规则后才能进行标注' };
  }
  if (fieldDetail.calculator_type === 3 && !fieldDetail.calculator_code) {
    return { pass: false, errMsg: '需设置标签的编码计算规则后才能进行标注' };
  }
  if (fieldDetail.calculator_type === 4 && (fieldDetail.regInfoArr || []).length < 1) {
    return { pass: false, errMsg: '需添加划词原文后才能进行标注' };
  }
  return { pass: true };
}

export function fetchCourseFilterTips({ instrumentType, filterList, paragraphArr, hideFilterName }) {
  const tips = [];
  const colon = ' : ';
  if (instrumentType) {
    const filterName = hideFilterName ? '' : `文书类型${colon}`;
    tips.push({ filterItem: { name: '文书类型' }, tipText: `${filterName}${instrumentType}` });
  }
  filterList.forEach(filterItem => {
    let tipText = '';
    const filterName = hideFilterName ? '' : `${filterItem.name}${colon}`;
    if (filterItem.inputType == 'version' && filterItem.value) {
      tipText = `${filterName}${filterItem.value}`;
    } else if (filterItem.inputType == 'matchSelect' && ['全文检索', '引用法条', '案由'].includes(filterItem.name)) {
      if (filterItem.name === '全文检索') {
        tipText = fullTextShowHandel(filterItem, paragraphArr).filter(i => i.text);
      }
      if (filterItem.name === '引用法条') {
        const paraObj = groupBy(filterItem.result, 'keyword');
        tipText = [];
        Object.keys(paraObj).forEach(paragraphKey => {
          const contentArr = paraObj[paragraphKey];
          let text = paragraphKey;
          if (text) {
            text = `${text} `;
          }
          contentArr.forEach((val, idx) => {
            let valText = val.value;
            if (idx !== 0 && valText) {
              valText = `${filterItem.type === 'and' ? ' + ' : ' | '} ${valText}`;
            }
            text = `${text}${valText}`;
          });
          if (text) {
            tipText.push({ text, contentArr });
          }
        });
      }
      if (filterItem.name === '案由') {
        const item = (filterItem.result || []).filter(paraItem => paraItem.value.length);
        if (item.length === 0) {
          return;
        }
        const val = item.map(i => {
          if (i.showValue.length > 1) {
            return `(${i.showValue.map(item => item.name).join(' | ')})`;
          } else {
            return `${i.showValue.map(item => item.name).join(' | ')}`;
          }
        });

        tipText = `${filterName}${val.join(filterItem.type === 'and' ? ' + ' : ' | ')}`;
        // 如果拆分成多个
        // tipText = item.map(i => {
        //   return { text:`${i.showValue.map(item => item.name).join(' | ')}`};
        // });
      }
    } else if (['审理程序', '地域及法院', '执法地域范围'].includes(filterItem.name) && filterItem.value.length > 0) {
      tipText = `${filterName}${filterItem.showValue.map(item => item.name).join(' / ')}`;
    } else if (filterItem.inputType == 'matchSelect' && filterItem.name == '引用法条') {
      filterItem.result.forEach(contentItem => {
        if (contentItem.keyword) {
          tipText = `${filterName}${contentItem.keyword}`;
          if (contentItem.value) {
            tipText = `${tipText} ${contentItem.value}`;
          }
        }
      });
    } else if (filterItem.inputType === 'province_city_cascader' && (filterItem.value || []).length > 0) {
      tipText = `${filterName}`;
      filterItem.value.forEach((val, i) => {
        tipText = `${tipText}${val[val.length - 1]}`;
        if (i !== filterItem.value.length - 1) {
          tipText = `${tipText}/`;
        }
      });
    } else if (filterItem.key === 'mulit_personal_crime') {
      const key = Object.keys(filterItem.value)
        .map(i => {
          return Number(filterItem.value[i]);
        })
        .join('');
      if (key > 0) {
        tipText = `${filterName}${crimeEnum(key)}`;
      }
    } else if (
      (filterItem.inputType == 'select' || filterItem.inputType == 'input') &&
      (filterItem.value || []).length > 0
    ) {
      tipText = `${filterName}`;
      if (Array.isArray(filterItem.value)) {
        filterItem.value.forEach((val, i) => {
          tipText = `${tipText}${val}`;
          if (i !== filterItem.value.length - 1) {
            tipText = `${tipText}|`;
          }
        });
      } else {
        tipText = `${tipText}${filterItem.value}`;
      }
    } else if (
      filterItem.inputType == 'date' &&
      filterItem.value.length > 0 &&
      (filterItem.value[0] || filterItem.value[1])
    ) {
      tipText = `${filterName}`;
      if (filterItem.value[0]) {
        tipText = `${tipText}${dayjs(filterItem.value[0]).format('YYYY-MM-DD')}`;
      }
      if (filterItem.value[1]) {
        if (tipText !== `${filterName}`) {
          tipText = `${tipText} - `;
        }
        tipText = `${tipText}${dayjs(filterItem.value[1]).format('YYYY-MM-DD')}`;
      }
    }
    if (tipText) {
      if (Array.isArray(tipText)) {
        tipText.forEach(textItem => {
          tips.push({ filterItem, tipText: textItem.text, contentArr: textItem.contentArr, id: textItem.id });
        });
      } else {
        tips.push({ filterItem, tipText });
      }
    }
  });
  return tips;
}

export function fullTextShowHandel(item, paragraphArr) {
  const type = {
    must: '且',
    should: '或'
  };
  const arr = [];
  item.result.forEach(i => {
    if (i.result && i.result.length) {
      arr.push({
        id: i.id,
        text: fullTextShowHandel(i, paragraphArr)
          .map(i => {
            return i.text;
          })
          .join(` ${type[i.type]} `)
      });
    } else if (i.value instanceof Array) {
      // 兼容案由数组情况
      const paraEnum = {
        'fullreason.keyword': '案由'
      };
      if (i.value.length) {
        arr.push({
          id: i.id,
          text: `${paraEnum[i.keyword]} : ${i.value.map(i => i.name).join(' | ')}`
        });
      }
    } else if (i.value) {
      const paraItem = paragraphArr.find(paraItem => paraItem.key === i.keyword);
      const para = paraItem ? paraItem.value : '全文';
      i.searchLimit = i.searchLimit || '';
      // 同篇不显示这两个文字
      // 全文 : 同段 xxxxx
      const txt = `${para}${i.searchLimit === '同篇' ? '' : ` ${i.searchLimit}`} : ${i.value}`;
      arr.push({
        id: i.id,
        text: txt
      });
    }
  });

  return arr;
}

export function getFullTextList(item) {
  const arr = [];
  if (item.result && item.result.length) {
    item.result.forEach(o => {
      arr.push(...this.getFullTextList(o));
    });
  } else if (item.value instanceof Array) {
    if (item.value.length) {
      arr.push(item);
    }
  } else if (item.value.trim()) {
    arr.push(item);
  }
  return arr;
}

export function fetchAxisMaxAndInterval({ series, axisArr, isYAxis }) {
  const baseNum = 50;
  const splitNum = 5;
  const maxAndInterval = [];
  axisArr.forEach((axisItem, axisIndex) => {
    const axisData = [];
    const axisName = isYAxis ? 'yAxisIndex' : 'xAxisIndex';
    series.forEach(serieItem => {
      if (axisIndex === serieItem[axisName]) {
        axisData.push(...serieItem.data);
      }
    });
    let maxNum = max(axisData);
    if (maxNum > baseNum) {
      maxNum = Math.ceil(maxNum / baseNum) * baseNum;
    } else {
      maxNum = Math.ceil(maxNum / splitNum) * splitNum;
    }
    const interval = maxNum / splitNum;
    maxNum = maxNum + interval;
    maxAndInterval.push({ max: maxNum, interval });
  });
  return maxAndInterval;
}

export function fetchFieldsQuery(fieldDetail) {
  return { type: fieldDetail.type, field_name: fieldDetail.field_name, deputy_field_type: fieldDetail.deputy_type };
}

export function detectZoom() {
  let ratio = 0;
  const screen = window.screen;
  const ua = navigator.userAgent.toLowerCase();

  if (window.devicePixelRatio !== undefined) {
    ratio = window.devicePixelRatio;
  } else if (~ua.indexOf('msie')) {
    if (screen.deviceXDPI && screen.logicalXDPI) {
      ratio = screen.deviceXDPI / screen.logicalXDPI;
    }
  } else if (window.outerWidth !== undefined && window.innerWidth !== undefined) {
    ratio = window.outerWidth / window.innerWidth;
  }

  if (ratio) {
    ratio = Math.round(ratio * 100) / 200;
  }

  return ratio;
}

export function formatProductInfo(productItem) {
  let dataSize = 0;
  let minDataSize = 0;
  let expirationTimeDelta = 0;
  let versionLevel = 0;
  let isSuperModel = false;
  productItem.productList.forEach(subItem => {
    const charge = JSON.parse(subItem.chargeStr || '{}');
    if (!versionLevel) {
      versionLevel = charge.versionLevel;
    }
    if (!isSuperModel) {
      isSuperModel = charge.isSuperModel;
    }
    if (!dataSize) {
      dataSize = charge.courseDataSetMaxNum;
    }
    if (!minDataSize) {
      minDataSize = charge.courseDataSetMinNum;
    }
    if (!expirationTimeDelta) {
      expirationTimeDelta = charge.expirationTimeDelta || 0;
    }
  });
  return {
    id: productItem.id,
    label: productItem.name,
    orderIds: [],
    price: productItem.couponDiscountPrice || productItem.payPrince,
    dataSize,
    minDataSize,
    isSuperModel,
    isFree: !versionLevel,
    versionLevel,
    expirationTimeDelta
  };
}

export function checkUsedOfResearch({ courseDetail, columnKey }) {
  let unPassIdx = -1;
  try {
    unPassIdx = (courseDetail._source.chart || []).findIndex(item => {
      const checkFcArr = [
        () =>
          [...(item.fields || []), ...(item.rows || []), ...(item.columns || [])].findIndex(
            subItem => subItem.columnKey === columnKey
          ),
        () => [...(item.statistics || [])].findIndex(subItem => subItem.fieldDetail.columnKey === columnKey),
        () =>
          JSON.parse(item.conditionTemp || '[]').findIndex(subItem => {
            if (subItem.isTextFilter) {
              return false;
            } else if (subItem.isLabelFilter) {
              return subItem.info.fieldDetail.columnKey === columnKey;
            }
            return subItem.columnInfo.columnKey === columnKey;
          }),
        () => {
          if (!item.sortInfo || (item.sortInfo.action || 'none') === 'none') {
            return -1;
          }
          return item.sortInfo.fieldInfo.columnKey === columnKey ? 1 : -1;
        }
      ];

      return checkFcArr.findIndex(fc => fc() !== -1) !== -1;
    });
  } catch (err) {
    reporter.log({ message: err.stack || err.message });
  }
  return unPassIdx;
}

export function fixIdStr(id = '') {
  if (!id) {
    return '';
  }
  return id.split('-').join('_');
}

export function exportExcel(sheet, fileName) {
  openDownloadDialog(sheet2blob(sheet), fileName);
}

function sheet2blob(sheet, sheetName) {
  sheetName = sheetName || 'sheet1';
  const workbook = {
    SheetNames: [sheetName],
    Sheets: {}
  };
  workbook.Sheets[sheetName] = sheet;
  // 生成excel的配置项
  const wopts = {
    bookType: 'xlsx', // 要生成的文件类型
    bookSST: false, // 是否生成Shared String Table，官方解释是，如果开启生成速度会下降，但在低版本IOS设备上有更好的兼容性
    type: 'binary'
  };
  const wbout = Xlsx.write(workbook, wopts);
  const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
  // 字符串转ArrayBuffer
  return blob;
}

function s2ab(s) {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i != s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
  return buf;
}

export function openDownloadDialog(url, saveName) {
  if (typeof url == 'object' && url instanceof Blob) {
    url = URL.createObjectURL(url); // 创建blob地址
  }
  const aLink = document.createElement('a');
  aLink.href = url;
  aLink.download = saveName || ''; // HTML5新增的属性，指定保存文件名，可以不要后缀，注意，file:///模式下不会生效
  let event;
  if (window.MouseEvent) event = new MouseEvent('click');
  else {
    event = document.createEvent('MouseEvents');
    event.initMouseEvent('click', true, false, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
  }
  aLink.dispatchEvent(event);
}

export function groupByFieldFilter(fields) {
  const fieldGroup = [
    { type: 'num', list: [], label: '数值', img: numImg },
    { type: 'date', list: [], label: '日期', img: dateImg },
    { type: 'text', list: [], label: '一般标签', img: textImg, tip: '' },
    { type: 'dict', list: [], label: '分类标签', img: dictImg },
    { type: 'defendantNames', list: [], label: '主体标签', img: nerImg },
    { type: 'numSort', list: [], label: '编号标签', img: sortImg }
  ];

  const fieldGroupIdxEnum = {
    num: 0,
    date: 1,
    text: 2,
    dict: 3,
    defendantNames: 4,
    numSort: 5
  };

  fields.forEach(item => {
    if (item.type === 'num') {
      if (item.deputy_type === 'entity_sort') {
        fieldGroup[fieldGroupIdxEnum.numSort].list.push(item);
      } else {
        fieldGroup[fieldGroupIdxEnum.num].list.push(item);
      }
    }

    if (item.type === 'date') {
      fieldGroup[fieldGroupIdxEnum.date].list.push(item);
    }

    if (item.type === 'text') {
      if (item.deputy_type === 'dict') {
        fieldGroup[fieldGroupIdxEnum.dict].list.push(item);
      } else if (['entity_ner', 'entity_natural_person'].includes(item.deputy_type)) {
        fieldGroup[fieldGroupIdxEnum.defendantNames].list.push(item);
      } else {
        fieldGroup[fieldGroupIdxEnum.text].list.push(item);
      }
    }
  });

  return fieldGroup;
}

export function crimeEnum(type) {
  return {
    '1000': '单主体',
    '0100': '多主体',
    '0010': '单案由',
    '0001': '多案由',
    '1010': '单主体 + 单案由',
    '1001': '单主体 + 多案由',
    '0101': '多主体 + 多案由',
    '0110': '多主体 + 单案由',
    '1100': '单主体 | 多主体',
    '0011': '单案由 | 多案由',
    '1111': '(单主体 | 多主体) + (单案由 | 多案由)',
    '1110': '(单主体 | 多主体) + 单案由',
    '1011': '单主体 + (单案由 | 多案由)',
    '1101': '(单主体 | 多主体) + 多案由',
    '0111': '多主体 + (单案由 | 多案由)'
  }[type];
}

export function couldGroupFiled(fieldDetail) {
  if (['defendantNames', 'party', 'agent'].includes(fieldDetail.field_name)) {
    return true;
  }
  return ['dict', 'entity_ner', 'entity_natural_person'].includes(fieldDetail.deputy_type);
}

export function errFunc(options) {
  let errMsg = '';
  let errStack = '';
  let isRequestErr = false;
  if (typeof options === 'string') {
    errMsg = options;
  } else {
    isRequestErr = !!options.code;
    errMsg = options.message || '此处报错';
    errStack = options.stack;
  }

  const reportInfo = { message: errStack || errMsg };
  if (isRequestErr) {
    reportInfo.code = options.code;
    reportInfo.requestInfo = options.requestInfo;
  }
  reporter.log(reportInfo);

  if (options.code === 502) {
    Message({ type: 'warning', message: '服务器正在重启，请稍候刷新页面', duration: 3000 });
    return;
  }

  if (errMsg === '未绑定手机号' || errMsg.includes('invalid dom')) {
    return;
  }

  console.error(options);

  if (errMsg.includes('会话已过期')) {
    Message.warning(errMsg);
  } else {
    if (errMsg === 'task polling cancel') {
      return;
    }
    if (errMsg.includes("Cannot read property 'refresh' of undefined")) {
      return;
    }
    if (errMsg.includes('Cannot read properties of undefined')) {
      return;
    }
    Message.error(errMsg);
  }
}

export function upgradeFieldGroupRelationId(staticFields, allFields) {
  if (!staticFields) {
    return allFields.map(fieldDetail => ({ ...fieldDetail }));
  }

  const remoteFieldEnum = {};
  allFields.forEach(fieldDetail => {
    remoteFieldEnum[fieldDetail._id] = fieldDetail;
  });
  return staticFields.map(fieldDetail => {
    return {
      ...fieldDetail,
      ...(remoteFieldEnum[fieldDetail._id] || {})
    };
  });
}

export function sortFields(fields, sortIds = []) {
  const sortArr = sortIds
    .map(fieldId => {
      return fields.find(fieldDetail => fieldDetail._id === fieldId);
    })
    .filter(o => !!o);

  return uniqBy([...sortArr, ...fields], '_id');
}

export const getSortWayOptions = fieldDetail => {
  if (!fieldDetail) {
    return [];
  }
  if (couldGroupFiled(fieldDetail)) {
    if (fieldDetail.deputy_type === 'dict') {
      return [
        { value: 'DICT_COUNT', label: '按分类统计数排序' },
        { value: 'DICT_NAME', label: '按分类名称排序' }
      ];
    } else {
      return [
        { value: 'ENTITY_NAME', label: '按名称排序' },
        { value: 'ENTITY_COUNT', label: '按名称统计数排序' }
      ];
    }
  }
  const wayEnum = {
    text: [
      { value: 'NULL_LAST', label: '先有值后为空' },
      { value: 'NULL_FIRST', label: '先为空后有值' }
    ],
    num: [
      { value: 'NUM_ASC', label: '按数值升序' },
      { value: 'NUM_DESC', label: '按数值降序' }
    ],
    date: [
      { value: 'DATE_ASC', label: '按日期升序' },
      { value: 'DATE_DESC', label: '按日期降序' }
    ]
  };
  return wayEnum[fieldDetail.type] || [];
};

export function fetchFieldIcon(data) {
  const fieldIconEnum = {
    text: { img: require('@/assets/img/fieldTypeImgs/一般标签@2x.png'), color: '#3e91f3' },
    dict: { img: require('@/assets/img/fieldTypeImgs/分类标签@2x.png'), color: '#40bace' },
    ner: { img: require('@/assets/img/fieldTypeImgs/主体标签@2x.png'), color: '#6b39e7' },
    date: { img: require('@/assets/img/fieldTypeImgs/日期@2x.png'), color: '#FFA938' },
    num: { img: require('@/assets/img/fieldTypeImgs/数值@2x.png'), color: '#04C8BB' },
    sort: { img: require('@/assets/img/fieldTypeImgs/编号@2x.png'), color: '#f4c12b' }
  };

  if (data.deputy_type === 'dict') {
    return fieldIconEnum.dict;
  }

  if (['entity_ner', 'entity_natural_person'].includes(data.deputy_type)) {
    return fieldIconEnum.ner;
  }

  if (data.deputy_type === 'entity_sort') {
    return fieldIconEnum.sort;
  }

  return fieldIconEnum[data.type] || {};
}

export function transformFieldToAnalysisMsg({ mode, fieldDetail, dataStr, relationFilterName }) {
  if (mode === 'statisticMode') {
    if (relationFilterName) {
      return `(${relationFilterName})`;
    }
    return '';
  }

  const { type } = fieldDetail;

  if (type === 'text') {
    return getRowDefaultCategoryMode({ ...fieldDetail, valueOfFilter: dataStr }) ? '(分类分组)' : '(一般分组)';
  }

  const valueOfFilter = JSON.parse(dataStr || '{}');

  if (fieldDetail.type === 'date') {
    const dateEnum = {
      year: '年',
      quarter: '季',
      month: '月',
      week: '周',
      day: '日'
    };
    return `(${valueOfFilter.fixedValue ? dateEnum[valueOfFilter.fixedValue] : dateEnum.year})`;
  }
  if (fieldDetail.type === 'num') {
    if (!valueOfFilter.type || valueOfFilter.type === 'auto') {
      return `(每间隔${valueOfFilter.valueSplit || 10}进行分组)`;
    }
    if (valueOfFilter.type === 'noRange') {
      return '(不分组)';
    }
    return '(自定义分组)';
  }
  return '';
}
